import { API_URL } from '@/lib/config';
import i18n from '@/i18n';
import { ApiError, refreshTokens } from './auth';
import { getRefreshToken, saveRefreshToken, clearRefreshToken } from '@/lib/secureStore';
import { useAuthStore } from '@/features/auth/authStore';

/** Every request declares the UI language; the server localizes its response. */
const langHeaders = () => ({ 'Accept-Language': i18n.language });

/** Parse a fetch Response into T, throwing ApiError on the standard error envelope. */
export async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? {};
    throw new ApiError(
      err.message ?? 'Something went wrong.',
      err.code ?? 'error',
      res.status,
      err.details,
      res.headers.get('content-language') ?? undefined,
    );
  }
  return data as T;
}

/** Unauthenticated GET (e.g. the public ticket-verify / scan endpoint). */
export function publicGet<T>(path: string): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    headers: { ...langHeaders() },
  }).then((r) => handle<T>(r));
}

/** Unauthenticated POST. Used by the public ticket verification (SEC-21), where
 *  the credential moved out of the URL and into the body — a token in a path is
 *  copied into every access log and proxy cache between here and the server. */
export function publicPost<T>(path: string, body: unknown): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...langHeaders() },
    body: JSON.stringify(body),
  }).then((r) => handle<T>(r));
}

// --- silent refresh on 401 --------------------------------------------------
// The access token is short-lived (~15 min); the refresh token is not. Without
// the block below, the moment the access token expired mid-session every
// authenticated request 401'd with no recovery until the app was reloaded (the
// token is minted only at cold boot). A 401 now triggers exactly one silent
// refresh + retry; if that also fails the session really is over, so we clear it
// and drop to `unauthenticated` (the root layout returns to sign-in).

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the stored refresh token for a fresh access token, updating the store
 * and persisting the rotated refresh token. Returns the new access token, or
 * null when there is nothing to restore / the refresh itself failed.
 *
 * Single-flight: a burst of parallel 401s (many queries load at once) must
 * trigger ONE refresh, not one per request — the server rotates refresh tokens
 * single-use, so concurrent refreshes would revoke each other and force a
 * spurious sign-out.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const refresh = await getRefreshToken();
        if (!refresh) return null;
        const tokens = await refreshTokens(refresh);
        await saveRefreshToken(tokens.refresh_token);
        useAuthStore.setState({ accessToken: tokens.access_token });
        return tokens.access_token;
      } catch {
        await clearRefreshToken();
        useAuthStore.setState({ status: 'unauthenticated', accessToken: null, me: null });
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

/**
 * Send an authenticated request; on a 401 do exactly one silent refresh and
 * retry with the fresh token. `send` rebuilds the request from the token each
 * call, so the retry carries the same method/body/idempotency key — dropping an
 * Idempotency-Key on the retry would turn a refresh into a 400 on a money write.
 */
async function authFetch<T>(send: (token: string) => Promise<Response>, token: string): Promise<T> {
  const res = await send(token);
  if (res.status !== 401) return handle<T>(res);
  const fresh = await refreshAccessToken();
  if (fresh) return handle<T>(await send(fresh));
  return handle<T>(res); // refresh failed — surface the original 401
}

export function authGet<T>(path: string, token: string): Promise<T> {
  return authFetch<T>(
    (tok) => fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${tok}`, ...langHeaders() } }),
    token,
  );
}

/** A fresh key for one logical request — the server dedupes retries of a money POST by this. */
export function newIdempotencyKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function authPost<T>(
  path: string,
  token: string,
  body?: unknown,
  opts?: { idempotencyKey?: string },
): Promise<T> {
  return authFetch<T>(
    (tok) =>
      fetch(`${API_URL}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tok}`,
          ...langHeaders(),
          ...(opts?.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    token,
  );
}

/** PUT — a whole-object replace. Distinct from authPatch because the API means
 *  different things by them: PATCH edits named fields, PUT replaces the record,
 *  and sending one where the other is expected silently drops the fields the
 *  caller did not mention. */
export function authPut<T>(path: string, token: string, body: unknown): Promise<T> {
  return authFetch<T>(
    (tok) =>
      fetch(`${API_URL}${path}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}`, ...langHeaders() },
        body: JSON.stringify(body),
      }),
    token,
  );
}

export function authPatch<T>(path: string, token: string, body: unknown): Promise<T> {
  return authFetch<T>(
    (tok) =>
      fetch(`${API_URL}${path}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}`, ...langHeaders() },
        body: JSON.stringify(body),
      }),
    token,
  );
}

/** DELETE. Takes an optional idempotency key because some deletes move money —
 *  cancelling a ticket is a refund — and a retried request must not pay twice. */
export function authDelete<T>(path: string, token: string, opts?: { idempotencyKey?: string }): Promise<T> {
  return authFetch<T>(
    (tok) =>
      fetch(`${API_URL}${path}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${tok}`,
          ...(opts?.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : {}),
          ...langHeaders(),
        },
      }),
    token,
  );
}
